import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { useNavigate } from "react-router-dom";
import { getAllStories } from "../../apiCalls/stories/api_v1_stories";
import { getAllShorts } from "../../apiCalls/shorts/api_v1_shorts";
import { getAllPodcasts } from "../../apiCalls/podcasts/api_v1_podcasts";
import { loadProTip } from "../../apiCalls/protip/api_v1_protip";
import {
  readListCache,
  writeListCache,
} from "../../utils/sessionListCache";
import { GaImage } from "../../components/ui/GaImage";
import {
  getMediaThumbnail,
  getPodcastThumbnail,
} from "../../utils/podcastThumbnail";
import { shortTitle } from "../../utils/shortTitle";
import { LocalizedTitle } from "../../components/common/RomanText";
import { getPodcastTitle } from "./homeCopy";
import fireImg from "../../assets/img/2 MAIN SCREENS EN/home/ic-fire-small.svg";
import micImg from "../../assets/img/2 MAIN SCREENS EN/home/ic-microphone.svg";
import logoImg from "../../assets/img/2 MAIN SCREENS EN/home/logo.svg";
import {
  EggBadge,
  OutlineButton,
  SectionSub,
  SectionTitle,
  sectionPx,
  useCountdown,
  useLangCopy,
  useUiLang,
} from "./homeUi";
import { MediaRail } from "./MediaRail";
import { PosterCard, clipFrameSx, podcastFrameSx } from "./PosterCard";
import { StoryBubble } from "./StoryBubble";
import { HomeChallengeRow } from "./HomeChallengeRow";
import { selectHomeChallenges } from "./selectHomeChallenges";
import { openChallenge } from "./challengeDestination";
import {
  CHALLENGES_LIST_CACHE_KEY,
  loadChallenges,
  normalizeChallenges,
} from "../../apiCalls/challenges/api_v1_challenges";
import CallofDuty from "../../assets/img/news/img_callofduty.avif";
import Fortnite from "../../assets/img/news/img_fortnite.avif";
import Valorant from "../../assets/img/news/img_valorant.avif";
import Freefire from "../../assets/img/news/img_freefire.avif";
import Minecraft from "../../assets/img/news/img_minecraft.avif";
import Pubg from "../../assets/img/news/img_pubg.avif";
import { MythOrFactCard } from "./mythFact/MythOrFactCard";
import { WrongAnswerScreen } from "./mythFact/WrongAnswerScreen";
import { MythFactNoticeDialog } from "./mythFact/MythFactNoticeDialog";
import {
  loadMythFact,
  normalizeMythFact,
  resolveMythFactScreen,
  submitMythFactAnswer,
} from "../../apiCalls/mythFact/api_v1_myth_fact";

const images = {
  "Call of Duty": CallofDuty,
  Fortnite,
  Valorant,
  Freefire,
  Minecraft,
  PUBG: Pubg,
};

export function TodaysChallenges({ games = [], skillLevel }) {
  const t = useLangCopy();
  const navigate = useNavigate();
  const expires = useCountdown();
  const tipGame = games[0];
  const [mythResult, setMythResult] = useState(null);
  const [mythQuestion, setMythQuestion] = useState(null);
  const [mythReady, setMythReady] = useState(false);
  const [mythAnswer, setMythAnswer] = useState(null);
  const [mythNotice, setMythNotice] = useState("");
  const [mythNoticeOpen, setMythNoticeOpen] = useState(false);
  const [mythSubmitting, setMythSubmitting] = useState(false);
  const [tipProTip, setTipProTip] = useState(null);
  const [tipReady, setTipReady] = useState(false);
  const [challengeTasks, setChallengeTasks] = useState([]);
  const [challengesReady, setChallengesReady] = useState(false);
  const myth = t.mythFact;

  const showMythCard = mythReady && Boolean(mythQuestion?.question);
  const showTipCard = tipReady && Boolean(tipProTip?.uuid);
  const showChallengeList = challengesReady && challengeTasks.length > 0;
  const showSection = showMythCard || showTipCard || showChallengeList;
  const tipImage =
    tipProTip?.thumbnail || images[tipGame?.name] || Pubg;
  const tipLabel = tipGame?.name || "Pro Tip";
  // Mobile + desktop: half/half when both APIs return data; full width when only one.
  const cardColumns =
    showMythCard && showTipCard
      ? {
          xs: "1fr 1fr",
          md: "minmax(0, 1fr) minmax(0, 1fr)",
          lg: "minmax(0, 1fr) minmax(0, 1fr)",
        }
      : { xs: "1fr", md: "1fr", lg: "1fr" };

  useEffect(() => {
    let active = true;
    let retryTimer = null;

    const pullChallenges = () => {
      // Always hit /challenges on home enter so claimed/reward state stays live.
      loadChallenges({ force: true })
        .then((result) => {
          if (!active) return;
          if (Array.isArray(result?.tasks)) {
            setChallengeTasks(result.tasks);
            setChallengesReady(true);
          } else {
            setChallengeTasks([]);
            setChallengesReady(true);
          }
        })
        .catch((error) => {
          console.error("Challenges load failed:", error);
          const recovered = normalizeChallenges(error?.response?.data);
          if (!active) return;
          if (Array.isArray(recovered?.tasks) && recovered.tasks.length > 0) {
            setChallengeTasks(recovered.tasks);
            setChallengesReady(true);
            return;
          }
          // No usable challenges payload — hide the list.
          setChallengeTasks([]);
          setChallengesReady(true);
        });
    };

    const pullMyth = () => {
      loadMythFact({ force: true })
        .then((question) => {
          if (!active) return;
          if (question?.question) {
            setMythQuestion(question);
            setMythNotice("");
            setMythNoticeOpen(false);
            setMythReady(true);
            return;
          }
          // API responded without a question — do not show the card.
          setMythQuestion(null);
          setMythReady(true);
          if (question?.message) setMythNotice(question.message);
        })
        .catch((error) => {
          const question = normalizeMythFact(error?.response?.data);
          if (!active) return;
          if (question?.question) {
            setMythQuestion(question);
            setMythNotice("");
            setMythNoticeOpen(false);
            setMythReady(true);
            return;
          }
          setMythQuestion(null);
          setMythReady(true);
          if (question?.message) setMythNotice(question.message);
          console.error("Myth fact load failed:", error);
        });
    };

    const pullTips = () => {
      loadProTip()
        .then((tip) => {
          if (!active) return;
          setTipProTip(tip?.uuid ? tip : null);
          setTipReady(true);
        })
        .catch(() => {
          if (!active) return;
          setTipProTip(null);
          setTipReady(true);
        });
    };

    const pullAll = () => {
      pullChallenges();
      pullMyth();
      pullTips();
    };

    pullAll();
    // Retry once if the first request raced token refresh on a cold start.
    if (!sessionStorage.getItem("accessToken")) {
      retryTimer = window.setTimeout(pullAll, 700);
    }

    if (window.location.hash === "#myth-fact") {
      document.getElementById("myth-fact")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    // Refresh only after a real claim — not on every tab focus/visibility flip.
    window.addEventListener("challengeClaimed", pullChallenges);

    return () => {
      active = false;
      window.clearTimeout(retryTimer);
      window.removeEventListener("challengeClaimed", pullChallenges);
    };
  }, [skillLevel]);

  const handleMythAnswer = async (choice) => {
    if (mythSubmitting) return;
    setMythSubmitting(true);
    try {
      const result = await submitMythFactAnswer(choice, mythQuestion?.questionId);
      const screen = resolveMythFactScreen(result);

      if (screen === "correct") {
        // Correct → EggCollectedDialog only (announced in submitMythFactAnswer).
        setMythAnswer(result);
        setMythNotice("");
        setMythNoticeOpen(false);
        setMythResult(null);
        return;
      }

      if (screen === "wrong") {
        // Wrong → WrongAnswerScreen only (no +N → EGG line).
        setMythAnswer(result);
        setMythNotice("");
        setMythNoticeOpen(false);
        setMythResult("wrong");
        return;
      }

      const message = result?.message || "";
      if (message) {
        setMythNotice(message);
        setMythNoticeOpen(true);
      }
    } catch (error) {
      console.error("Myth fact answer failed:", error);
      const message = error?.response?.data?.message;
      if (message) {
        setMythNotice(String(message));
        setMythNoticeOpen(true);
      }
    } finally {
      setMythSubmitting(false);
    }
  };

  const homeChallenges = selectHomeChallenges(challengeTasks);
  const challengesDone = challengeTasks.filter((task) => task.claimed).length;
  const challengesTotal = challengeTasks.length;
  const challengesSub = challengesTotal
    ? t.challengesSub.replace(/\d+/, String(challengesTotal))
    : t.challengesSub;

  // Wait for APIs: hide empty Myth/Fact, Pro Tip, or challenges blocks.
  if (!showSection) return null;

  const openTipCard = () => {
    if (tipProTip?.uuid) {
      navigate("/tips", { state: { proTip: tipProTip } });
      return;
    }
    navigate("/tips");
  };

  return (
    <Box sx={{ pt: 0, pb: 1, bgcolor: "#0D0F0A" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: "16px",
          px: sectionPx,
          py: { xs: "13px", md: "18px" },
          minHeight: { xs: 62, md: 72 },
          boxSizing: "border-box",
          border: "1px dashed rgba(255, 255, 255, 0.2)",
        }}
      >
        <Box sx={{ ml: { xs: "12px", md: "16px" } }}>
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "2px",
              color: "#B8EE3C",
              lineHeight: "13px",
            }}
          >
            {t.rewardEyebrow}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              mt: "3px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 12,
                color: "#F2FAE6",
                lineHeight: "18px",
              }}
            >
              {t.rewardBody}
            </Typography>
            <Box
              component="img"
              src={micImg}
              alt=""
              sx={{ width: 13, height: 13 }}
            />
          </Box>
        </Box>
        <Box sx={{ textAlign: "right", mr: { xs: 1.5, md: 1.5 } }}>
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: 9,
              letterSpacing: "1.5px",
              color: "#9AA88A",
              lineHeight: "11px",
            }}
          >
            {t.expires}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              color: "#F9A825",
              lineHeight: "19px",
              mt: "3px",
            }}
          >
            {expires}
          </Typography>
        </Box>
      </Box>

      {(showChallengeList || showMythCard || showTipCard) && (
        <Box
          sx={{ textAlign: "center", pt: { xs: "22px", md: "28px" }, pb: "8px" }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", gap: "8px" }}>
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: { xs: 26, md: 32 },
                letterSpacing: "2px",
                color: "#FFFFFF",
                lineHeight: 1.2,
              }}
            >
              {t.challengesTitle[0]}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: { xs: 26, md: 32 },
                letterSpacing: "2px",
                color: "#B8EE3C",
                lineHeight: 1.2,
              }}
            >
              {t.challengesTitle[1]}
            </Typography>
          </Box>
          {showChallengeList ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                mt: "6px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 12.5,
                  color: "#9AA88A",
                }}
              >
                {challengesSub}
              </Typography>
              <Box
                component="img"
                src={fireImg}
                alt=""
                sx={{ width: 12, height: 12 }}
              />
              <Box
                sx={{
                  border: "1px solid #262B1C",
                  px: "9px",
                  py: "4px",
                  bgcolor: "#15170F",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 700,
                    fontSize: 13,
                    color: "#B8EE3C",
                  }}
                >
                  {challengesDone}/{challengesTotal}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}

      {(showMythCard || showTipCard) && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: cardColumns,
            gap: { xs: "12px", md: "20px" },
            px: sectionPx,
            mt: { xs: "16px", md: "20px" },
          }}
        >
          {showMythCard ? (
            <Box id="myth-fact" sx={{ minWidth: 0 }}>
              <MythOrFactCard
                title={t.mythTitle}
                prompt={mythQuestion?.question || mythNotice}
                disabled={mythSubmitting}
                mythLabel={t.myth}
                factLabel={t.fact}
                onAnswer={handleMythAnswer}
                sx={{ position: "relative", zIndex: 2 }}
              />
            </Box>
          ) : null}

          {showTipCard ? (
            <Box
              onClick={openTipCard}
              className="neon-wrapper"
              sx={{
                minWidth: 0,
                height: { xs: 186, md: 240, lg: 260 },
                cursor: "pointer",
                clipPath:
                  "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)",
              }}
            >
              <Box
                className="neon-inner"
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "#15170F",
                  p: { xs: "13px", md: "20px" },
                  boxSizing: "border-box",
                  clipPath:
                    "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: "1px",
                      color: "#B8EE3C",
                    }}
                  >
                    {t.tipTitle}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {tipProTip?.rewardAvailable ? <EggBadge /> : null}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mt: "16px",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 96, md: 124, lg: 140 },
                      height: { xs: 96, md: 124, lg: 140 },
                      borderRadius: "50%",
                      p: "3px",
                      background: "linear-gradient(180deg, #B8EE3C, #4A6A12)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "4px solid #0D0F0A",
                      }}
                    >
                      <Box
                        component="img"
                        src={tipImage}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: "1px",
                      color: "#FFFFFF",
                      mt: "7px",
                    }}
                  >
                    {tipLabel}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}

      {showChallengeList ? (
        <>
          <Box
            sx={{
              mt: { xs: "16px", md: "20px" },
              px: sectionPx,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: "8px", md: "16px" },
            }}
          >
            {homeChallenges.map((task) => (
              <HomeChallengeRow
                key={task.taskKey || task.position}
                task={task}
                onClick={() => openChallenge(navigate, task)}
              />
            ))}
          </Box>
          {challengesTotal >= 3 ? (
            <Box sx={{ mt: "8px", width: "100%" }}>
              <OutlineButton
                label={t.viewAll}
                onClick={() => navigate("/challenges")}
              />
            </Box>
          ) : null}
        </>
      ) : null}
      <WrongAnswerScreen
        open={mythResult === "wrong"}
        onClose={() => setMythResult(null)}
        accent={myth.wrongLabel}
        rest={myth.answer}
        message={mythAnswer?.message || ""}
        reward=""
        cta={myth.gotIt}
      />
      <MythFactNoticeDialog
        open={mythNoticeOpen}
        message={mythNotice}
        cta={myth.gotIt}
        onClose={() => setMythNoticeOpen(false)}
      />
    </Box>
  );
}

export function ProTipStories({ skillLevel }) {
  const t = useLangCopy();
  const cacheKey = `ga_home_stories_${skillLevel}`;
  const [stories, setStories] = useState(() => readListCache(cacheKey) || []);
  const navigate = useNavigate();
  useEffect(() => {
    const cached = readListCache(cacheKey);
    if (cached?.length) setStories(cached);
    getAllStories({ skillLevel }, "/home", { useCache: false })
      .then((response) => {
        const items = response.data?.data?.stories || [];
        if (items.length > 0) {
          setStories(items);
          writeListCache(cacheKey, items);
        } else {
          setStories([]);
        }
      })
      .catch(() => {
        /* keep cache; hide when empty */
      });
  }, [skillLevel, cacheKey]);

  const openStory = (index) => {
    navigate("/stories", {
      state: { stories, currIndex: index },
    });
  };

  if (stories.length === 0) return null;

  return (
    <Box sx={{ pt: "26px", pb: "6px", bgcolor: "#0D0F0A" }}>
      <SectionTitle>{t.storiesTitle}</SectionTitle>
      <SectionSub>{t.storiesSub}</SectionSub>
      <MediaRail
        items={stories}
        getKey={(story) => story.uuid}
        spaceBetween={12}
        gap={{ xs: "12px", md: "16px", lg: "20px" }}
        swiperSx={{
          "&, & .swiper-slide, & .swiper-slide:focus": {
            outline: "none",
            WebkitTapHighlightColor: "transparent",
          },
        }}
        renderItem={(story, index) => (
          <StoryBubble
            title={story.title}
            thumbnail={story.thumbnail}
            eager={index === 0}
            onClick={() => openStory(index)}
          />
        )}
      />
    </Box>
  );
}


export function Podcast({ skillLevel }) {
  const t = useLangCopy();
  const lang = useUiLang();
  const cacheKey = `ga_home_podcasts_${skillLevel}`;
  const [podcasts, setPodcasts] = useState(() => readListCache(cacheKey) || []);
  const navigate = useNavigate();
  useEffect(() => {
    const cached = readListCache(cacheKey);
    if (cached) setPodcasts(cached);
    getAllPodcasts({ skillLevel }, { useCache: false })
      .then((response) => {
        const items = response.data?.data?.podcasts || [];
        if (items.length > 0) {
          setPodcasts(items);
          writeListCache(cacheKey, items);
        }
      })
      .catch(() => {
        // Keep session/state cache; never wipe the rail on 429/errors.
      });
  }, [skillLevel, cacheKey]);

  return (
    <Box sx={{ pt: "26px", pb: "6px", bgcolor: "#0D0F0A" }}>
      <SectionTitle>{t.podcastTitle}</SectionTitle>
      <SectionSub>{t.podcastSub}</SectionSub>
      <MediaRail
        items={podcasts}
        getKey={(item) => item.uuid}
        spaceBetween={10}
        breakpoints={{
          0: { spaceBetween: 10 },
          900: { spaceBetween: 14 },
          1200: { spaceBetween: 16 },
        }}
        renderItem={(podcast, index) => (
          <PosterCard
            src={getPodcastThumbnail(podcast)}
            fallback={Pubg}
            eager={index === 0}
            imageWidth={280}
            imageHeight={158}
            frameSx={podcastFrameSx}
            onClick={() => navigate(`/guides?uuid=${podcast.uuid}`)}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(transparent 40%, rgba(0,0,0,.85))",
                display: "flex",
                alignItems: "flex-end",
                p: "8px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: 12, md: 15 },
                  color: "#FFFFFF",
                  lineHeight: 1.2,
                }}
              >
                {getPodcastTitle(podcast.title, lang)}
              </Typography>
            </Box>
          </PosterCard>
        )}
      />
      <Box sx={{ mt: "14px" }}>
        <OutlineButton
          label={t.allEpisodes}
          onClick={() => navigate("/guides")}
        />
      </Box>
    </Box>
  );
}


export function Gameplay({ skillLevel }) {
  const t = useLangCopy();
  const lang = useUiLang();
  const cacheKey = `ga_home_shorts_${skillLevel}`;
  const [shorts, setShorts] = useState(() => readListCache(cacheKey) || []);
  const navigate = useNavigate();
  useEffect(() => {
    const cached = readListCache(cacheKey);
    if (cached) setShorts(cached);
    getAllShorts({ skillLevel }, { useCache: false })
      .then((response) => {
        const items = response.data?.data?.shorts || [];
        if (items.length > 0) {
          setShorts(items);
          writeListCache(cacheKey, items);
        }
      })
      .catch(() => {
        // Keep session/state cache so desktop Gameplay stays visible after 429.
      });
  }, [skillLevel, cacheKey]);

  return (
    <Box sx={{ pt: "26px", pb: "6px", bgcolor: "#0D0F0A" }}>
      <SectionTitle>{t.gameplayTitle}</SectionTitle>
      <SectionSub>{t.gameplaySub}</SectionSub>
      <MediaRail
        items={shorts}
        getKey={(item) => item.uuid}
        spaceBetween={10}
        breakpoints={{
          0: { spaceBetween: 10 },
          900: { spaceBetween: 14 },
          1200: { spaceBetween: 16 },
        }}
        renderItem={(short, index) => (
          <PosterCard
            src={getMediaThumbnail(short)}
            fallback={Pubg}
            eager={index === 0}
            imageWidth={196}
            imageHeight={348}
            frameSx={clipFrameSx}
            onClick={() => navigate(`/bites?short=${short.uuid}`, { state: { short } })}
          >
            <Box sx={{ position: "absolute", left: 9, right: 9, bottom: 9 }}>
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: 12, md: 14 },
                  color: "#FFFFFF",
                  lineHeight: "16px",
                }}
              >
                <LocalizedTitle text={shortTitle(short)} lang={lang} />
              </Typography>
            </Box>
          </PosterCard>
        )}
      />
      <Box sx={{ mt: "14px" }}>
        <OutlineButton
          label={t.watchClips}
          onClick={() => navigate("/bites")}
        />
      </Box>
    </Box>
  );
}


export function GameFeatures({ games = [] }) {
  const t = useLangCopy();
  const navigate = useNavigate();
  const featureNames = {
    PUBG: "PUBG\nMOBILE",
    Freefire: "FREE\nFIRE",
    "Call of Duty": "CALL OF\nDUTY",
    Fortnite: "FORTNITE",
    Valorant: "VALORANT",
    Minecraft: "MINECRAFT",
  };

  return (
    <Box sx={{ pt: "26px", pb: "6px", bgcolor: "#0D0F0A" }}>
      <SectionTitle>{t.featuresTitle}</SectionTitle>
      <SectionSub>{t.featuresSub}</SectionSub>
      <MediaRail
        scrollOnly
        items={games}
        getKey={(game) => game.uuid}
        renderItem={(game, index) => (
          <PosterCard
            src={images[game.name] || Pubg}
            eager={index === 0}
            imageWidth={196}
            imageHeight={348}
            frameSx={clipFrameSx}
            onClick={() => navigate(`/game?uuid=${game.uuid}&game=${game.name}`)}
          >
            <Typography
              sx={{
                position: "absolute",
                left: 9,
                bottom: 9,
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 13,
                color: "#FFFFFF",
                lineHeight: "16px",
                whiteSpace: "pre-line",
              }}
            >
              {featureNames[game.name] || game.name}
            </Typography>
          </PosterCard>
        )}
      />
      <Box sx={{ mt: "14px" }}>
        <OutlineButton
          label={t.todaysFeature}
          onClick={() =>
            games[0]
              ? navigate(`/game?uuid=${games[0].uuid}&game=${games[0].name}`)
              : navigate("/game")
          }
        />
      </Box>
    </Box>
  );
}


export function HomeFooter() {
  const t = useLangCopy();
  const navigate = useNavigate();

  return (
    <Box sx={{ pt: "28px", pb: "24px", textAlign: "center" }}>
      <Box
        component="img"
        src={logoImg}
        alt="Gamez Academy"
        sx={{ width: 135, height: "auto" }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "14px",
          mt: "14px",
        }}
      >
        {t.footerNav.map((item, index) => (
          <Typography
            key={item}
            component="button"
            onClick={() =>
              navigate(["/", "/play", "/guides", "/news", "/rewards"][index])
            }
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "1px",
              color: "#9AA88A",
              cursor: "pointer",
              border: "none",
              bgcolor: "transparent",
            }}
          >
            {item}
          </Typography>
        ))}
      </Box>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: 11,
          color: "#666B5C",
          mt: "14px",
        }}
      >
        {t.footerLegal}
      </Typography>
    </Box>
  );
}

function syncDesktopNewsCards(swiper) {
  if (!swiper?.slides?.length) return;
  const perView =
    typeof swiper.params.slidesPerView === "number"
      ? swiper.params.slidesPerView
      : 1;

  swiper.slides.forEach((slide) => {
    const card = slide.querySelector("[data-news-card]");
    if (!card) return;
    const progress = typeof slide.progress === "number" ? slide.progress : 0;
    const minProgress = -(perView - 1);
    let outside = 0;
    if (progress > 0.02) outside = progress;
    else if (progress < minProgress - 0.02) outside = minProgress - progress;
    const amount = Math.min(outside, 1);
    const blur = amount * 8;
    const focused = amount < 0.04;

    card.style.filter = blur < 0.25 ? "none" : `blur(${blur.toFixed(2)}px)`;
    card.style.opacity = (1 - amount * 0.5).toFixed(3);
    card.style.transform = `scale(${(1 - amount * 0.08).toFixed(3)})`;
    card.style.borderColor = focused
      ? "rgba(255,255,255,0.55)"
      : "rgba(255,255,255,0.14)";
    card.style.boxShadow = focused
      ? "0 18px 36px rgba(0,0,0,0.48), 0 0 28px rgba(184,238,60,0.18)"
      : "0 8px 16px rgba(0,0,0,0.28)";
  });
}

export function News({ games = [], hideFooter = false }) {
  const t = useLangCopy();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width:900px)", { noSsr: true });
  const featured = games[0];
  const formatted = featured
    ? featured.name.toLowerCase().replace(/\s+/g, "-")
    : "";
  const newsCards = games.filter((game) => game?.name);

  if (isDesktop) {
    return (
      <Box sx={{ pt: "26px", pb: "8px", bgcolor: "#0D0F0A" }}>
        <SectionTitle>{t.newsTitle}</SectionTitle>
        <SectionSub>{t.newsSub}</SectionSub>
        {newsCards.length > 0 && (
          <Box
            sx={{
              mt: "16px",
              "& .swiper": {
                padding: "10px 2px 22px",
                overflow: "hidden",
              },
              "@media (min-width: 1100px) and (max-width: 1279.98px)": {
                "& .swiper": { maxWidth: 1080, mx: "auto" },
              },
              "@media (min-width: 1280px)": {
                "& .swiper": { maxWidth: 1560, mx: "auto" },
              },
              "& .swiper-slide": { height: "auto" },
              "& .swiper-pagination": {
                bottom: "0px !important",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "5px",
              },
              "& .swiper-pagination-bullet": {
                bgcolor: "#2A2E22",
                width: 7,
                height: 3,
                borderRadius: 0,
                opacity: 1,
                margin: "0 !important",
              },
              "& .swiper-pagination-bullet-active": {
                bgcolor: "#B8EE3C",
                width: 28,
              },
            }}
          >
            <Swiper
              slidesPerView={1}
              spaceBetween={14}
              watchSlidesProgress
              autoplay={{ delay: 2800, disableOnInteraction: false }}
              loop={newsCards.length > 1}
              pagination={{ clickable: true }}
              modules={[Autoplay, Pagination]}
              speed={680}
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 14 },
                1100: { slidesPerView: 2, spaceBetween: 16 },
                1280: { slidesPerView: 3, spaceBetween: 18 },
              }}
              onSwiper={syncDesktopNewsCards}
              onProgress={syncDesktopNewsCards}
              onResize={syncDesktopNewsCards}
              onBreakpoint={syncDesktopNewsCards}
            >
              {newsCards.map((game) => {
                const slug = game.name.toLowerCase().replace(/\s+/g, "-");
                return (
                  <SwiperSlide
                    key={game.uuid || game.name}
                    onClick={() => navigate(`/news/${slug}`)}
                    style={{ cursor: "pointer", height: "auto" }}
                  >
                    <Box
                      data-news-card
                      sx={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "16 / 10",
                        overflow: "hidden",
                        bgcolor: "#15170F",
                        borderRadius: "18px",
                        border: "1px solid rgba(255,255,255,0.55)",
                        boxShadow:
                          "0 18px 36px rgba(0,0,0,0.48), 0 0 28px rgba(184,238,60,0.18)",
                        transformOrigin: "center center",
                        willChange: "transform, opacity, filter",
                      }}
                    >
                      <GaImage
                        src={images[game.name] || Pubg}
                        alt=""
                        width={1600}
                        height={1000}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          aspectRatio: "16 / 10",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(transparent 42%, rgba(0,0,0,.88))",
                        }}
                      />
                      <Typography
                        sx={{
                          position: "absolute",
                          left: 14,
                          right: 14,
                          bottom: 14,
                          fontFamily: "'Rajdhani', sans-serif",
                          fontWeight: 700,
                          fontSize: { md: 15, lg: 17 },
                          lineHeight: 1.2,
                          color: "#FFFFFF",
                        }}
                      >
                        {`${game.name} — ${t.newsHeadline}`}
                      </Typography>
                    </Box>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </Box>
        )}
        <Box sx={{ mt: "14px" }}>
          <OutlineButton label={t.allNews} onClick={() => navigate("/news")} />
        </Box>
        {!hideFooter && <HomeFooter />}
      </Box>
    );
  }

  return (
    <Box sx={{ pt: "26px", pb: "8px", bgcolor: "#0D0F0A" }}>
      <SectionTitle>{t.newsTitle}</SectionTitle>
      <SectionSub>{t.newsSub}</SectionSub>
      {featured && (
        <Box
          onClick={() => navigate(`/news/${formatted}`)}
          sx={{
            mx: sectionPx,
            mt: "16px",
            height: { xs: 204, md: 280, lg: 340 },
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            clipPath:
              "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)",
          }}
        >
          <GaImage
            src={images[featured.name] || Pubg}
            alt=""
            eager
            width={960}
            height={534}
            sx={{
              width: "100%",
              height: "100%",
              aspectRatio: "960 / 534",
              objectFit: "cover",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(transparent 45%, rgba(0,0,0,.9))",
            }}
          />
          <Typography
            sx={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 12,
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: { xs: 16, md: 22, lg: 26 },
              lineHeight: 1.2,
              color: "#FFFFFF",
            }}
          >
            {featured.name
              ? `${featured.name} — ${t.newsHeadline}`
              : t.newsHeadline}
          </Typography>
        </Box>
      )}
      <Box sx={{ mt: "14px" }}>
        <OutlineButton label={t.allNews} onClick={() => navigate("/news")} />
      </Box>
      {!hideFooter && <HomeFooter />}
    </Box>
  );
}
